import type { ContractLegalStatusCode, StudioDocumentsRegistryRow } from '@/types'

/**
 * Общий слой юридического статуса и сроков ДКК для экранов реестра документов:
 * расширенный `/management/documents-registry` (advanced) и витрина для остальных
 * ролей читают одну и ту же логику. Второй реализации не заводить — иначе
 * «действует / истёк, признаётся» на двух экранах разойдётся.
 */

export const CONTRACT_STATUS_LABELS: Record<ContractLegalStatusCode, string> = {
  DRAFT: 'Не активирован',
  ACTIVE_SIGNED: 'Действует',
  EXPIRED_RECOGNIZED: 'Истёк, признаётся',
  ENDED: 'Не действует',
  NOT_STARTED: 'Не начался',
}

export function fmtDate(value: string | null): string {
  if (!value) return '—'
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return '—'
  return `${match[3]}.${match[2]}.${match[1]}`
}

export function formatContractDurationYears(start: string | null, end: string | null): string | null {
  if (!start || !end) return null
  const startDate = new Date(`${start.slice(0, 10)}T00:00:00Z`)
  const endDate = new Date(`${end.slice(0, 10)}T00:00:00Z`)
  if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime()) || endDate < startDate) {
    return null
  }

  // Contract end dates are inclusive, hence the extra day. A tropical year
  // keeps multi-year terms containing leap years from becoming 9.9 instead of 10.
  const inclusiveDays = Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1
  const roundedYears = Math.round((inclusiveDays / 365.2425) * 10) / 10
  const yearsLabel = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(roundedYears)
  // Дробное число согласуется с родительным единственного: «1,5 года», «2,5 года».
  if (!Number.isInteger(roundedYears)) return `${yearsLabel} года`
  const lastTwo = Math.abs(roundedYears) % 100
  const last = Math.abs(roundedYears) % 10
  const noun = lastTwo !== 11 && last === 1
    ? 'год'
    : (lastTwo < 12 || lastTwo > 14) && last >= 2 && last <= 4
      ? 'года'
      : 'лет'
  return `${yearsLabel} ${noun}`
}

export function todayIsoLocal(): string {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

export function getContractDates(row: StudioDocumentsRegistryRow) {
  const actualEnd = row.contract_actual_end_date
    ?? (row.contract_status === 'CLOSED' ? row.contract_end : null)
  return {
    signedAt: row.contract_signed_at ?? null,
    effectiveFrom: row.contract_effective_from ?? row.contract_start,
    expectedEnd: row.contract_expected_end_date ?? row.contract_end,
    actualEnd,
  }
}

export function deriveContractStatus(
  row: StudioDocumentsRegistryRow,
  today = todayIsoLocal(),
): ContractLegalStatusCode {
  // Заготовки нет в contract_legal_state_v вовсе, поэтому серверный статус у неё
  // пустой. Без этой ветки сработал бы вывод по датам и заготовка выглядела бы
  // действующим договором — ровно то, что активация и запрещает.
  if (row.contract_activated_at == null) return 'DRAFT'
  if (row.legal_status_code) return row.legal_status_code
  const dates = getContractDates(row)
  if (dates.actualEnd && today > dates.actualEnd) return 'ENDED'
  if (!dates.actualEnd && dates.expectedEnd && today > dates.expectedEnd) return 'EXPIRED_RECOGNIZED'
  if (dates.effectiveFrom && today < dates.effectiveFrom) return 'NOT_STARTED'
  return 'ACTIVE_SIGNED'
}

export function contractStatusTone(
  status: ContractLegalStatusCode,
): 'success' | 'warning' | 'error' | 'neutral' {
  if (status === 'ACTIVE_SIGNED') return 'success'
  if (status === 'EXPIRED_RECOGNIZED') return 'warning'
  if (status === 'DRAFT') return 'warning'
  return 'neutral'
}

/**
 * Действующие + истёкшие, но признаваемые действующими. Ровно эта популяция
 * видна на витрине; на расширенном экране — фильтр «Действующие».
 */
export function isActiveOrRecognized(row: StudioDocumentsRegistryRow): boolean {
  const status = deriveContractStatus(row)
  return status === 'ACTIVE_SIGNED' || status === 'EXPIRED_RECOGNIZED'
}

export type ExpiryCategory = 'OVERDUE' | 'LT_1M' | 'LT_3M' | 'LT_6M' | 'NO_DATE' | 'OK' | 'NONE'
export type ExpiryFilter = 'ALL' | 'EXPIRING' | 'OVERDUE' | 'LT_1M' | 'LT_3M' | 'LT_6M' | 'NO_DATE'

export function daysUntilIso(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00`)
  const to = new Date(`${toIso.slice(0, 10)}T00:00:00`)
  return Math.round((to.getTime() - from.getTime()) / 86400000)
}

// Expiry monitoring uses the canonical documentary end date
// (current_expected_end_date, which folds in ДС о продлении/расторжении),
// surfaced by getContractDates().expectedEnd. ENDED / NOT_STARTED are not
// monitored (category 'NONE'); already-expired-but-recognised contracts are
// 'OVERDUE'; active contracts without any end date are 'NO_DATE'.
export function getExpiryInfo(
  row: StudioDocumentsRegistryRow,
  today = todayIsoLocal(),
): { category: ExpiryCategory; daysLeft: number | null } {
  const status = deriveContractStatus(row, today)
  // У заготовки срок ещё не течёт: она не действует, пока не активирована.
  if (status === 'DRAFT') return { category: 'NONE', daysLeft: null }
  if (status === 'ENDED' || status === 'NOT_STARTED') return { category: 'NONE', daysLeft: null }
  const expectedEnd = getContractDates(row).expectedEnd
  if (status === 'EXPIRED_RECOGNIZED') {
    return { category: 'OVERDUE', daysLeft: expectedEnd ? daysUntilIso(today, expectedEnd) : null }
  }
  // ACTIVE_SIGNED
  if (!expectedEnd) return { category: 'NO_DATE', daysLeft: null }
  const daysLeft = daysUntilIso(today, expectedEnd)
  if (daysLeft <= 30) return { category: 'LT_1M', daysLeft }
  if (daysLeft <= 90) return { category: 'LT_3M', daysLeft }
  if (daysLeft <= 180) return { category: 'LT_6M', daysLeft }
  return { category: 'OK', daysLeft }
}

export const EXPIRY_BADGE: Record<
  Exclude<ExpiryCategory, 'NONE' | 'OK'>,
  { label: string; tone: 'error' | 'warning' | 'neutral' }
> = {
  OVERDUE: { label: 'Просрочен', tone: 'error' },
  LT_1M: { label: '< 1 мес', tone: 'error' },
  LT_3M: { label: '< 3 мес', tone: 'warning' },
  LT_6M: { label: '< 6 мес', tone: 'warning' },
  NO_DATE: { label: 'Без даты', tone: 'neutral' },
}

export function expirySortKey(row: StudioDocumentsRegistryRow): number {
  const { category, daysLeft } = getExpiryInfo(row)
  if (category === 'NONE') return Number.MAX_SAFE_INTEGER
  if (category === 'NO_DATE') return Number.MAX_SAFE_INTEGER - 1
  return daysLeft ?? Number.MAX_SAFE_INTEGER - 2
}

export function matchesExpiryFilter(row: StudioDocumentsRegistryRow, filter: ExpiryFilter): boolean {
  if (filter === 'ALL') return true
  const { category } = getExpiryInfo(row)
  if (filter === 'EXPIRING') {
    return category === 'OVERDUE' || category === 'LT_1M' || category === 'LT_3M' || category === 'LT_6M'
  }
  return category === filter
}

/**
 * Подпись франчайзи в гриде: «ФИО, № N» одной строкой (номер отдельной колонкой
 * не выносим — она съедала ширину и повторяла то же самое). Номер без ФИО и ФИО
 * без номера остаются читаемыми, поэтому склейка через фильтр, а не шаблон.
 */
export function formatFranchiseeLabel(row: StudioDocumentsRegistryRow): string {
  return [
    row.partner_fio ?? 'ФИО не указано',
    row.partner_number != null ? `№ ${row.partner_number}` : null,
  ]
    .filter(Boolean)
    .join(', ')
}
